// TopNav — redesigned top navigation header (replaces the left sidebar).
//
// Pixel-mapped from the design `.topnav` (employee-portal 2/project/styles.css
// + mobile.css). Layout: brand left · centered nav · right actions
// (Request access · Luna bell · user menu). On mobile (<768px) the centered nav
// is hidden — those destinations live in the MobileTabBar — and the actions
// compact down.
//
// Breakpoints use the design's exact px cutoffs (768/1024/380) via `sx` media
// queries rather than Chakra's em scale, so the responsive steps land where the
// design intends.

import { Box, Icon, Image } from "@chakra-ui/react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { TbPlus } from "react-icons/tb";
import { PloyLogoV2 } from "components/Logo";
import PloyButton from "components/ds/PloyButton";
import { RequestAccessModal } from "components/RequestAccess";
import MFMembers from "state/members/membersState";
import Linker from "ui/Linker";
import LunaBell from "./LunaBell";
import { DESKTOP_NAV, useVisibleNavItems, type NavItem } from "./navConfig";
import { isRequestAccessVisible } from "./navVisibility";
import UserMenu from "./UserMenu";
import { STICKY_CHROME_PROPS } from "./stickyChrome";

const CountPill = ({ count, active }: { count: number; active: boolean }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    minW="20px"
    h="18px"
    px="6px"
    fontSize="11px"
    fontWeight={600}
    borderRadius="component.md" // 6px
    bg={active ? "azurite.100" : "neutral.100"}
    color={active ? "azurite.700" : "neutral.800"}
  >
    {count}
  </Box>
);

const NavLink = ({
  item,
  active,
  count,
}: {
  item: NavItem;
  active: boolean;
  count?: number;
}) => (
  <Linker
    to={item.to}
    display="inline-flex"
    alignItems="center"
    gap="sm" // 8px
    fontFamily="body"
    fontWeight={500}
    color={active ? "azurite.600" : "neutral.800"}
    bg={active ? "azurite.50" : "transparent"}
    borderRadius="component.lg" // 8px
    transition="background 120ms, color 120ms"
    _hover={{ textDecoration: "none", bg: active ? "azurite.50" : "neutral.100" }}
    sx={{
      padding: "8px 14px",
      fontSize: "14px",
      "@media (max-width: 1024px)": { padding: "8px 10px", fontSize: "13px" },
    }}
  >
    {item.label}
    {count !== undefined && count > 0 && (
      <CountPill count={count} active={active} />
    )}
  </Linker>
);

const TopNav = () => {
  const { pathname } = useLocation();
  const me = MFMembers.useGetMe();
  const config = MFMembers.useGetPortalConfig();
  const stats = MFMembers.useGetStats();
  const navItems = useVisibleNavItems(DESKTOP_NAV);
  const requestAccessVisible = isRequestAccessVisible(
    config?.requestAccessEnabled,
  );
  const [requestOpen, setRequestOpen] = useState(false);

  const taskCount = stats
    ? (stats.provisioning || 0) +
      (stats.tasks || 0) +
      (stats.deprovisioning || 0) +
      (stats.entitlementAdjustment || 0)
    : 0;

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={50}
      {...STICKY_CHROME_PROPS}
      bg="neutral.25"
      borderBottom="1px solid"
      borderColor="neutral.200"
      display="flex"
      alignItems="center"
      sx={{
        height: "68px",
        padding: "0 40px",
        gap: "24px",
        "@media (max-width: 1024px)": { padding: "0 24px", gap: "16px" },
        "@media (max-width: 768px)": {
          height: "56px",
          padding: "0 14px",
          gap: "10px",
        },
        "@media (max-width: 380px)": { padding: "0 10px", gap: "6px" },
      }}
    >
      {/* Brand */}
      <Box display="flex" alignItems="center" flexShrink={0}>
        {config?.logoUrl ? (
          <Image
            src={config.logoUrl}
            alt=""
            w="auto"
            sx={{
              height: "28px",
              "@media (max-width: 768px)": { height: "22px" },
              "@media (max-width: 380px)": { height: "20px" },
            }}
          />
        ) : (
          /* Fallback only — orgs that uploaded their own logo never see this. */
          <PloyLogoV2 w="92px" />
        )}
      </Box>

      {/* Centered nav — hidden on mobile (lives in the bottom tab bar) */}
      <Box
        as="nav"
        mx="auto"
        display={{ base: "none", md: "flex" }}
        alignItems="center"
        sx={{ gap: "4px", "@media (max-width: 1024px)": { gap: "2px" } }}
      >
        {navItems.map((item) => (
          <NavLink
            key={item.id}
            item={item}
            active={item.isActive(pathname)}
            count={item.hasCount ? taskCount : undefined}
          />
        ))}
      </Box>

      {/* Right actions */}
      <Box
        display="flex"
        alignItems="center"
        flexShrink={0}
        sx={{
          gap: "12px",
          "@media (max-width: 768px)": { gap: "4px", marginLeft: "auto" },
        }}
      >
        {requestAccessVisible && (
          <PloyButton
            variant="primary"
            onClick={() => setRequestOpen(true)}
            leftIcon={
              <Icon as={TbPlus} boxSize={{ base: "16px", md: "14px" }} />
            }
            // Compact down on mobile: 36px tall, 0/12 padding, 13px.
            h={{ base: "36px", md: "auto" }}
            minH={{ base: "36px", md: "32px" }}
            px={{ base: "12px", md: "14px" }}
            py={{ base: "0", md: "8px" }}
            fontSize={{ base: "smd", md: "md" }}
          >
            Request access
          </PloyButton>
        )}
        <LunaBell />
        {me && <UserMenu me={me} />}
      </Box>

      {/* Mount only while open so the basket/recipient state resets each time —
          a kept-mounted modal would carry a prior session's items into the
          next request. */}
      {requestOpen && (
        <RequestAccessModal isOpen onClose={() => setRequestOpen(false)} />
      )}
    </Box>
  );
};

export default TopNav;
